/* Ombrance — atelier-core.js : client de l'API de l'Atelier (même origine, proxy d'application Shopify ; contrat
   docs/atelier/architecture.md), stockage local tolérant, analytics et utilitaires partagés par les composants
   atelier-studio, atelier-print, atelier-wall et atelier-creations. Aucune dépendance. Chargé en defer, avant eux. */

(() => {
  if (window.OmbranceAtelier) return;

  /* Le filtre Liquid t échappe le HTML (« l&#39;Atelier ») : décodage unique avant affichage par textContent */
  const ENTITIES = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'", '&#x27;': "'", '&#34;': '"' };
  const decode = (v) => {
    if (typeof v === 'string') return v.replace(/&(?:amp|lt|gt|quot|#39|#x27|#34);/g, (m) => ENTITIES[m]);
    if (Array.isArray(v)) return v.map(decode);
    if (v && typeof v === 'object') return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, decode(x)]));
    return v;
  };
  const readConfig = () => {
    try { return decode(JSON.parse(document.getElementById('AtelierConfig')?.textContent || '{}')); } catch (e) { return {}; }
  };
  const config = readConfig();
  const strings = config.strings || {};
  const base = String(config.apiBase || '/apps/atelier/api').replace(/\/+$/, '');

  const t = (key, vars = {}) => String(strings[key] ?? key).replace(/\{(\w+)\}/g, (m, k) => (k in vars ? vars[k] : m));
  const plural = (n, one, other) => t(Math.abs(n) < 2 ? one : other, { count: n });

  /* Stockage local : chaque accès peut échouer (navigation privée, données bloquées) ; rien d'indispensable n'y vit. */
  const PREFIX = 'ombrance.atelier.';
  const memory = {};
  const store = {
    get(key) {
      try { const v = localStorage.getItem(PREFIX + key); return v ? JSON.parse(v) : null; } catch (e) { return memory[key] ?? null; }
    },
    set(key, value) {
      memory[key] = value;
      try { localStorage.setItem(PREFIX + key, JSON.stringify(value)); } catch (e) { /* mémoire seule */ }
    },
    del(key) {
      delete memory[key];
      try { localStorage.removeItem(PREFIX + key); } catch (e) { /* rien à faire */ }
    }
  };

  const uuid = () => {
    if (window.crypto?.randomUUID) return crypto.randomUUID();
    const b = crypto.getRandomValues(new Uint8Array(16));
    b[6] = (b[6] & 15) | 64; b[8] = (b[8] & 63) | 128;
    const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
  };

  /* Identifiant d'appareil anonyme : un indice parmi d'autres pour le serveur (Turnstile, IP, plafonds), jamais une preuve. */
  const deviceId = (() => {
    let id = store.get('device');
    if (typeof id !== 'string' || id.length < 16) { id = uuid(); store.set('device', id); }
    return id;
  })();

  class ApiError extends Error {
    constructor(code, status = 0, message = '', data = null, retryAfter = 0) {
      super(message || code);
      this.code = code; this.status = status; this.data = data; this.retryAfter = retryAfter;
    }
  }

  const query = (params) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') q.set(k, v); });
    const s = q.toString();
    return s ? `?${s}` : '';
  };

  /* raw : corps binaire (photo), envoyé tel quel avec son type */
  async function request(path, { method = 'GET', body, raw, headers = {}, timeout = 20000 } = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    let res;
    try {
      res = await fetch(base + path, {
        method,
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json', ...(body && !raw ? { 'Content-Type': 'application/json' } : {}), ...headers },
        body: raw || (body ? JSON.stringify(body) : undefined),
        signal: controller.signal
      });
    } catch (e) {
      throw new ApiError(navigator.onLine === false ? 'OFFLINE' : 'NETWORK');
    } finally {
      clearTimeout(timer);
    }
    const type = res.headers.get('content-type') || '';
    let data = null;
    if (type.includes('json')) { try { data = await res.json(); } catch (e) { data = null; } }
    if (!res.ok) {
      /* Proxy absent ou non configuré : Shopify répond par une page HTML (404) → service indisponible */
      const code = data?.code || (data ? `HTTP_${res.status}` : 'UNAVAILABLE');
      throw new ApiError(code, res.status, data?.message, data, Number(res.headers.get('Retry-After')) || 0);
    }
    if (res.status === 204) return {};
    if (!data) throw new ApiError('UNAVAILABLE', res.status);
    return data;
  }

  const enc = encodeURIComponent;
  const api = {
    me: () => request(`/me${query({ deviceId })}`),
    /* Clé d'idempotence en en-tête ET dans le corps : le proxy d'application ne garantit pas la transmission des en-têtes */
    generate: (payload, key) => request('/generations', { method: 'POST', body: { ...payload, deviceId, idempotencyKey: key }, headers: { 'Idempotency-Key': key }, timeout: 30000 }),
    generation: (id) => request(`/generations/${enc(id)}${query({ deviceId })}`),
    /* Photo (déjà réencodée par le navigateur) : envoyée seulement après la case de consentement cochée */
    uploadPhoto: (blob) => request(`/photos${query({ deviceId, consent: '1' })}`, { method: 'POST', raw: blob, headers: { 'Content-Type': blob.type || 'image/jpeg' }, timeout: 60000 }),
    deletePhoto: (id) => request(`/photos/${enc(id)}${query({ deviceId })}`, { method: 'DELETE' }),
    claim: (id) => request(`/artworks/${enc(id)}/claim`, { method: 'POST', body: { deviceId } }),
    artworks: (cursor) => request(`/artworks${query({ cursor })}`),
    favorite: (id, favorite) => request(`/artworks/${enc(id)}/favorite`, { method: 'POST', body: { favorite, deviceId } }),
    remove: (id) => request(`/artworks/${enc(id)}`, { method: 'DELETE' }),
    download: (id) => request(`/artworks/${enc(id)}/download${query({ deviceId })}`),
    printOptions: (id) => request(`/artworks/${enc(id)}/print-options${query({ deviceId })}`)
  };

  /* Normalisation défensive (camelCase ou snake_case) des objets renvoyés par l'API */
  const num = (v) => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? null : Number(v));
  const normArtwork = (a) => {
    if (!a || !a.id) return null;
    const width = num(a.width);
    const height = num(a.height);
    return {
      id: String(a.id),
      previewUrl: a.previewUrl || a.preview_url || a.preview || '',
      width, height,
      aspect: a.aspect || a.orientation || (width && height ? (Math.abs(width / height - 1) < 0.04 ? 'square' : width > height ? 'landscape' : 'portrait') : 'portrait'),
      styleId: a.styleId || a.style_id || '',
      styleName: a.styleName || a.style_name || '',
      description: a.description || '',
      favorite: Boolean(a.favorite),
      createdAt: a.createdAt || a.created_at || null
    };
  };
  /* Prix : priceCents (entier) s'il est fourni, sinon price en euros (« 5.00 ») */
  const cents = (o) => {
    if (num(o.priceCents ?? o.price_cents) !== null) return num(o.priceCents ?? o.price_cents);
    const euros = num(String(o.price ?? '').replace(',', '.'));
    return euros === null ? 0 : Math.round(euros * 100);
  };
  const normPack = (p) => ({
    id: String(p.id ?? p.sku ?? p.credits),
    credits: num(p.credits) || 0,
    price: cents(p),
    variantId: p.available === false ? null : (p.variantId || p.variant_id || null),
    popular: Boolean(p.popular || p.badge),
    bestValue: Boolean(p.bestValue)
  });
  const normStyle = (s) => ({ id: String(s.id), name: s.name || '', description: s.description || '', preview: s.preview || s.preview_url || '', options: s.options || null, defaults: s.defaults || null, photo: s.photo !== false });
  /* Création à partir d'une photo : absente d'un serveur plus ancien → mode masqué */
  const normPhoto = (p) => (p && typeof p === 'object' && p.enabled ? {
    credits: num(p.credits) || 1,
    proposals: num(p.proposals) || 1,
    freeAvailable: Boolean(p.freeAvailable),
    maxBytes: num(p.maxBytes) || 15 * 1048576,
    minPx: num(p.minPx) || 512,
    enhance: Boolean(p.enhance),
    enhanceBelowPx: num(p.enhanceBelowPx) || 1024,
    ttlHours: num(p.ttlHours) || 24
  } : null);
  /* Affinage : absent d'un serveur plus ancien → fonction masquée */
  const normRefine = (r) => (r && typeof r === 'object' ? {
    freePerCreation: num(r.freePerCreation) || 0,
    freeProposals: num(r.freeProposals) || 1,
    paidProposals: num(r.paidProposals) || 1,
    max: num(r.max) || 120
  } : null);
  const normMe = (m) => ({
    loggedIn: Boolean(m.loggedIn ?? m.logged_in),
    balance: num(m.balance) || 0,
    freeTrialAvailable: Boolean(m.freeTrialAvailable ?? m.free_trial_available),
    /* Créations offertes restantes (serveur antérieur : 1 si disponible) */
    freeTrialsRemaining: num(m.freeTrialsRemaining) ?? (m.freeTrialAvailable ? 1 : 0),
    freeTrialsTotal: num(m.freeTrialsTotal) || 1,
    freeTrialReason: m.freeTrialReason || '',
    proposals: num(m.proposals) || 1,
    refine: normRefine(m.refine),
    photo: normPhoto(m.photo),
    packs: Array.isArray(m.packs) ? m.packs.map(normPack).filter((p) => p.credits > 0) : [],
    styles: Array.isArray(m.styles) ? m.styles.map(normStyle) : [],
    options: m.options || null,
    claimable: Array.isArray(m.claimable) ? m.claimable.map(String) : []
  });
  const normPrint = (o) => ({
    variantId: o.available === false ? null : (o.variantId || o.variant_id),
    label: o.label || o.format || '',
    price: cents(o),
    format: o.format || '',
    kind: o.kind || '',
    orientation: o.orientation || ''
  });

  /* Prix en centimes → « 5 € », « 0,65 € » */
  const money = (cents) => {
    const value = cents / 100;
    try {
      return new Intl.NumberFormat(config.locale || 'fr-FR', {
        style: 'currency', currency: config.currency || 'EUR', minimumFractionDigits: Number.isInteger(value) ? 0 : 2
      }).format(value);
    } catch (e) { return `${value.toFixed(2).replace('.', ',')} €`; }
  };

  /* Images servies par le CDN Shopify : largeur à la demande (paramètre width) */
  const sized = (url, width) => {
    if (!url) return '';
    try {
      const u = new URL(url, location.origin);
      if (u.hostname === 'cdn.shopify.com' || u.pathname.includes('/cdn/shop/')) {
        u.searchParams.set('width', String(width));
        return u.toString();
      }
    } catch (e) { /* URL invalide : rendue telle quelle */ }
    return url;
  };
  const srcset = (url, widths) => widths.map((w) => `${sized(url, w)} ${w}w`).join(', ');

  /* Événements clients Shopify (préfixe « atelier: », convention du pixel personnalisé) + dataLayer s'il existe.
     Aucune donnée personnelle : jamais la description saisie. */
  const track = (name, data = {}) => {
    try {
      const r = window.Shopify?.analytics?.publish?.(`atelier:${name}`, data);
      if (r && typeof r.catch === 'function') r.catch(() => {});
    } catch (e) { /* l'analytics ne doit jamais casser l'Atelier */ }
    try {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') window.dataLayer.push({ event: name, ...data });
    } catch (e) { /* idem */ }
  };

  const loginUrl = (returnTo) => `${config.loginUrl || '/customer_authentication/login'}?return_to=${enc(returnTo)}`;

  const announce = (message) => {
    const region = document.getElementById('LiveRegion');
    if (!region) return;
    region.textContent = '';
    setTimeout(() => { region.textContent = message; }, 50);
  };

  const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches || document.body.dataset.animations !== 'true';

  const openDialog = (dialog, trigger) => {
    if (!dialog || dialog.open) return;
    dialog._trigger = trigger || document.activeElement;
    dialog.showModal();
  };

  const scripts = {};
  const loadScript = (src) => {
    if (!scripts[src]) {
      scripts[src] = new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = src; s.async = true;
        s.onload = resolve; s.onerror = () => { delete scripts[src]; reject(new Error('script')); };
        document.head.appendChild(s);
      });
    }
    return scripts[src];
  };

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  window.OmbranceAtelier = {
    config, t, plural, store, uuid, deviceId, api, ApiError, normArtwork, normMe, normPrint, money, sized, srcset,
    track, loginUrl, announce, reduceMotion, openDialog, loadScript, sleep
  };
})();
